import { resolveIconUrl } from "./cdnUtils";

/**
 * Loads map icons on demand. Layers reference icons by their stored path
 * (`icons/imw/Camp.png`, `public-icons/org_…/fire.png`); when MapLibre meets one
 * it hasn't got, it fires `styleimagemissing` and this fetches the image from
 * wherever `resolveIconUrl` says it lives. Used by the main map and the preview
 * map, so both resolve and load icons the same way.
 */

// Matches absolute URLs, root-relative assets, data URLs and relative asset paths.
const isLikelyImageUrl = (id: string) =>
  /^(https?:\/\/|\/|data:image\/)/i.test(id) || /\//.test(id) || /\.(png|jpe?g|webp|gif|svg)$/i.test(id);

// MapLibre warns for every image it could not load; those are expected while an
// icon is still loading or falls back, so they are filtered out once, globally.
let warningsFiltered = false;
const filterImageLoadWarnings = () => {
  if (warningsFiltered) return;
  warningsFiltered = true;
  const originalWarn = console.warn;
  console.warn = (...args: any[]) => {
    if (args[0]?.includes?.("could not be loaded")) return;
    originalWarn.apply(console, args);
  };
};

/** Attaches the loader to a MapLibre map; returns a function that detaches it. */
export const attachMissingImageLoader = (map: any): (() => void) => {
  if (map._culturemapImageHandlerAdded) return () => {};
  map._culturemapImageHandlerAdded = true;
  filterImageLoadWarnings();

  const loadingImages = new Set<string>();
  const failedImages = new Set<string>();

  // A 1px transparent image stops MapLibre asking again for an icon that can't load.
  const addTransparentFallback = (id: string) => {
    if (!id || map.hasImage(id)) return;
    try {
      map.addImage(id, new ImageData(new Uint8ClampedArray([0, 0, 0, 0]), 1, 1));
    } catch (error: any) {
      if (!error?.message?.includes("already exists")) {
        console.warn(`[MapImages] Failed to add transparent fallback image for ${id}:`, error);
      }
    }
  };

  const handleStyleImageMissing = (e: any) => {
    const id = e.id;
    if (!id) return;
    if (failedImages.has(id)) {
      addTransparentFallback(id);
      return;
    }
    if (loadingImages.has(id) || map.hasImage(id)) return;
    if (!isLikelyImageUrl(id)) {
      failedImages.add(id);
      addTransparentFallback(id);
      return;
    }

    loadingImages.add(id);
    fetch(resolveIconUrl(id))
      .then((response) => {
        if (!response.ok) throw new Error(`Image fetch failed (${response.status})`);
        const contentType = response.headers.get("content-type") || "";
        if (contentType && !contentType.startsWith("image/")) throw new Error(`Unexpected content-type: ${contentType}`);
        return response.blob();
      })
      .then((blob) => {
        if (!blob.type.startsWith("image/")) throw new Error(`Blob is not an image: ${blob.type || "unknown"}`);
        return createImageBitmap(blob);
      })
      .then((imageBitmap) => {
        if (!map.hasImage(id)) {
          try {
            map.addImage(id, imageBitmap);
          } catch (error: any) {
            if (!error.message?.includes("already exists")) console.warn(`[MapImages] Error adding image ${id}:`, error);
          }
        }
        loadingImages.delete(id);
      })
      .catch((error) => {
        failedImages.add(id);
        addTransparentFallback(id);
        console.warn(`[MapImages] Failed to load image ${id}:`, error);
        loadingImages.delete(id);
      });
  };

  map.on("styleimagemissing", handleStyleImageMissing);
  return () => {
    map.off("styleimagemissing", handleStyleImageMissing);
    map._culturemapImageHandlerAdded = false;
  };
};
